import {
  GIFT_FLOW_RPL_STEP,
  GIFT_FLOW_EXCHANGE_STEP,
  GIFT_FLOW_SCHEDULE_STEP,
  GIFT_FLOW_INFORMATION_STEP,
  GIFT_FLOW_SHIPPING_STEP,
  GIFT_FLOW_CONFIRMATION_STEP,
  GIFT_FLOW_THANKS_STEP,
  GIFT_FLOW_DONATE_STEP,
  GIFT_FLOW_EXCHANGE_ID,
  GIFT_FLOW_DONATE_ID,
} from '../../../../constants/giftFlow.constants';
import { MARKETPLACE_DONATE_ROUTE, MARKETPLACE_ITEMS_ROUTE } from '../../constants/marketplaceRoute.constants';
import { IProduct } from '../../types/Product.types';
import { IStatuses } from '../statuses/statuses.types';

import { IFlowStatus } from './giftFlow.types';

export interface IGiftFlowStatusesParams {
  hashId: string;
  flags: IStatuses;
  flowProduct: IProduct;
  sentProduct: IProduct;
  isDonationFlow: boolean;
  activeStatus: number;
  isGift: boolean;
  useMarketplaceStep?: boolean;
}

/**
 * Create array of enabled statuses based on gift options.
 * @param {string} hashId - Gift hashId
 * @param {Object} flags - Available statuses flags.
 * @param {boolean} flags.actions - Does this gift have RA
 * @param {boolean} flags.address - Is this gift with address required
 * @param {boolean} flags.draft - Is it a draft mode
 * @param {boolean} flags.exchange - Was product exchanged during claim
 * @param {boolean} flags.meeting - Does this gift have meeting RA
 * @param {boolean} flags.preview - Is it a preview mode
 * @param {Object} flowProduct - Product in the gift flow
 * @param {Object} sentProduct - Initial sent product to recipient
 * @param {boolean} isDonationFlow - Show if user uses donation flow
 * @param {integer} activeStatus - Current active status in the gift flow
 * @param {boolean} isGift - Is the invitation type is gift (We have 3 types: gift, code, alyceCard)
 * @param {boolean} useMarketplaceStep - Show if recipient uses marketplace step
 */
export const getGiftFlowStatuses = ({
  hashId,
  flags,
  flowProduct,
  sentProduct,
  isDonationFlow,
  activeStatus,
  isGift,
  useMarketplaceStep = false,
}: IGiftFlowStatusesParams): IFlowStatus[] => {
  const statuses = [{ ...GIFT_FLOW_RPL_STEP, route: `/${hashId}` }];
  const { meeting, address, actions } = flags;

  const isProductChanged = sentProduct && flowProduct && sentProduct.id !== flowProduct.id;
  const isProductAddressRequired = isProductChanged ? flowProduct.isAddressRequired : address;
  const isCodeAddressRequired = !isGift;

  if (useMarketplaceStep) {
    const viewRoute = isDonationFlow ? MARKETPLACE_DONATE_ROUTE : MARKETPLACE_ITEMS_ROUTE;
    const marketplaceStep = isDonationFlow ? GIFT_FLOW_DONATE_STEP : GIFT_FLOW_EXCHANGE_STEP;
    statuses.push({
      ...marketplaceStep,
      route: `/${hashId}/${GIFT_FLOW_EXCHANGE_STEP.route}/${viewRoute}`,
    });
  }

  if (meeting) {
    statuses.push({
      ...GIFT_FLOW_SCHEDULE_STEP,
      route: `/${hashId}/${GIFT_FLOW_SCHEDULE_STEP.route}`,
    });
  }

  if (actions) {
    statuses.push({
      ...GIFT_FLOW_INFORMATION_STEP,
      route: `/${hashId}/${GIFT_FLOW_INFORMATION_STEP.route}`,
    });
  }

  const showShippingStep = isProductAddressRequired || isCodeAddressRequired;
  const isMarketplaceStep = activeStatus === GIFT_FLOW_EXCHANGE_ID || activeStatus === GIFT_FLOW_DONATE_ID;
  const isAddressRequired =
    (!useMarketplaceStep && showShippingStep) || (useMarketplaceStep && !isMarketplaceStep && showShippingStep);

  if (isAddressRequired) {
    statuses.push({
      ...GIFT_FLOW_SHIPPING_STEP,
      route: `/${hashId}/${GIFT_FLOW_SHIPPING_STEP.route}`,
    });
  }

  statuses.push({
    ...GIFT_FLOW_CONFIRMATION_STEP,
    route: `/${hashId}/${GIFT_FLOW_CONFIRMATION_STEP.route}`,
  });

  statuses.push({
    ...GIFT_FLOW_THANKS_STEP,
    route: `/${hashId}/${GIFT_FLOW_THANKS_STEP.route}`,
  });

  return statuses;
};
